/**
 * Filtros e agrupamento para abas Pendente/Liberado (LCMY_ASSTEC02 condt_*).
 * PENDENTE/LIBERADO podem chegar como ids (view=card); resolve a partir dos grupos.
 * Encaminhada (já em sessão/ao vivo): não entra nas abas — só a sessão.
 */

import {
  extractBoardOrderId,
  hydrateTechnicianCondtAuxGroups,
  isCondtAuxIdRef,
} from './hydrateTechnicianCondtAuxGroups';

/** Seções da coluna do técnico cuja união alimenta Pendente/Liberado (independente do bucket FUTURA). */
const TECHNICIAN_BOARD_SECTIONS_MAIN = [
  'Em deslocamento',
  'Em serviço',
  'Previsto para hoje',
  'Previstas para amanhã',
  'FUTURA',
];
const CONDT_AUX_GROUPS = new Set(['PENDENTE', 'LIBERADO']);

/**
 * Todas as OS do técnico nas seções do board, deduplicadas (condt_* vem da mesma linha LCMY_ASSTEC02).
 * Inbox PENDENTE/LIBERADO só se a OS ainda não estiver em sessão/ao vivo.
 */
export function getAllTechnicianOrdersForCondtTabs(technicianGroups, technician) {
  const raw = technicianGroups?.[technician];
  const g = hydrateTechnicianCondtAuxGroups(raw);
  if (!g) return [];
  const byId = new Map();

  for (const section of TECHNICIAN_BOARD_SECTIONS_MAIN) {
    for (const o of g[section] || []) {
      const key = extractBoardOrderId(o);
      if (!key) continue;
      if (!byId.has(key)) {
        byId.set(key, { order: o, section });
      }
    }
  }

  for (const section of ['PENDENTE', 'LIBERADO']) {
    for (const item of g[section] || []) {
      const key = extractBoardOrderId(item);
      if (!key) continue;
      if (byId.has(key)) continue;
      if (isCondtAuxIdRef(item)) continue;
      byId.set(key, { order: item, section });
    }
  }

  return Array.from(byId.values()).map(({ order, section }) => ({
    ...order,
    boardTechnicianSection: section,
  }));
}

export function matchesCondtPendente(o) {
  if (!CONDT_AUX_GROUPS.has(o?.boardTechnicianSection)) return false;
  const cr = o?.condt_return;
  const ca = o?.condt_requests_available;
  if (cr == null || String(cr).trim() === '') return false;
  const n = Number(ca);
  return Number.isFinite(n) && n === 0;
}

export function matchesCondtLiberado(o) {
  if (!CONDT_AUX_GROUPS.has(o?.boardTechnicianSection)) return false;
  const cr = o?.condt_return;
  const rq = o?.condt_requests;
  const avail = o?.condt_requests_available;
  if (cr == null || String(cr).trim() === '') return false;
  const nRq = Number(rq);
  const nAvail = Number(avail);
  if (!Number.isFinite(nRq) || !Number.isFinite(nAvail)) return false;
  if (nRq <= 0) return false;
  return nRq === nAvail;
}

export function groupOrdersByCity(orders) {
  const groups = {};
  (orders || []).forEach((ordem) => {
    const cidade = ordem.cidade || 'Sem Cidade';
    if (!groups[cidade]) groups[cidade] = [];
    groups[cidade].push(ordem);
  });
  return groups;
}

/**
 * Listas Pendente/Liberado após um matcher (ex.: busca global + SLA Filtrar).
 */
export function computeFuturaCondtTabListsWithMatcher(technicianGroups, technician, matcher) {
  const condt = getAllTechnicianOrdersForCondtTabs(technicianGroups, technician);
  const pendente = condt.filter(matchesCondtPendente).filter(matcher);
  const liberado = condt.filter(matchesCondtLiberado).filter(matcher);
  return { pendente, liberado };
}

/**
 * Escolhe aba Retorno com busca ativa; preserva aba atual se ainda tiver itens quando há empate.
 */
export function pickFuturaRetornoTabForSearch(pendente, liberado, currentTab) {
  const pn = pendente.length;
  const ln = liberado.length;
  if (pn === 0 && ln === 0) return null;
  if (pn > 0 && ln === 0) return 'pendente';
  if (ln > 0 && pn === 0) return 'liberado';
  if (currentTab === 'liberado' && ln > 0) return 'liberado';
  if (currentTab === 'pendente' && pn > 0) return 'pendente';
  return 'pendente';
}
